import type { RepwiseEvent } from './events.ts';
import { importedWorkouts } from './import.ts';
import { formatMass } from './mass.ts';
import { workoutExtras, workoutLogs } from './programme.ts';

/**
 * CSV export (US-16) and backup restore (US-28). One CSV row per set, Repwise, imported and hand-added workouts
 * alike, so the file opens cleanly in a spreadsheet. Restore accepts our own JSON backup and the R2 backup.
 */
const NAME: Record<string, string> = { squat: 'Squat', bench: 'Bench Press', row: 'Barbell Row', ohp: 'Overhead Press', deadlift: 'Deadlift', curl: 'Dumbbell Curl', pushup: 'Push-ups' };

const cell = (v: string | number | null | undefined): string => {
  const s = v === null || v === undefined ? '' : String(v);
  // Neutralise spreadsheet formulas (CSV injection): anything starting with = + - @ tab or CR that isn't a plain
  // number gets a leading apostrophe. Review M1: "-1+cmd|..." used to slip through as "numeric".
  const numeric = /^-?\d+(\.\d+)?$/.test(s);
  const safe = !numeric && /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r\t]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** Each row carries its own unit: weights are never silently converted in an export. */
export function toCsv(events: readonly RepwiseEvent[], timeZone = 'UTC'): string {
  const day = (ms: number, tz = timeZone) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(ms);
  const time = (ms: number) => new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false }).format(ms);
  const rows: { at: number; cells: (string | number | null)[] }[] = [];
  const extras = workoutExtras(events);
  for (const w of workoutLogs(events)) {
    const minutes = Math.round((w.endedAt - w.startedAt) / 60_000);
    const x = extras.get(w.workoutId);
    const seen = new Set<string>();
    for (const s of w.sets) {
      // The workout note (and bodyweight) on its first row; each exercise note on that exercise's first row.
      const first = seen.size === 0;
      const exFirst = !seen.has(s.exercise);
      seen.add(s.exercise);
      const note = [first ? x?.note : undefined, first && x?.bodyweight ? `Bodyweight ${formatMass(x.bodyweight.weight)} ${x.bodyweight.unit}` : undefined, exFirst ? x?.exercises[s.exercise] : undefined].filter(Boolean).join(' · ');
      rows.push({
        at: w.startedAt,
        cells: [day(w.startedAt), time(w.startedAt), `Workout ${w.kind}`, 'Repwise', NAME[s.exercise] ?? s.exercise, s.kind === 'warmup' ? 'Warm-up' : s.kind === 'work' ? 'Work' : s.kind === 'extra' ? 'Extra' : 'Accessory', s.index + 1, s.weight === null ? '' : formatMass(s.weight), w.unit, s.reps ?? 'skipped', s.target, minutes, note],
      });
    }
  }
  for (const w of importedWorkouts(events)) {
    w.exercises.forEach((x, xi) =>
      x.reps.forEach((r, i) =>
        rows.push({
          at: w.performedAt,
          cells: [day(w.performedAt, 'UTC'), '', w.name, ({ manual: 'Added by hand', stronglifts: 'StrongLifts import', strong: 'Strong import', hevy: 'Hevy import' } as const)[w.source], x.exercise ? (NAME[x.exercise] ?? x.name) : x.name, 'Work', i + 1, x.weight === null ? '' : formatMass(x.weight), w.unit, r ?? '', '', xi === 0 && i === 0 ? (w.note ?? '') : ''],
        }),
      ),
    );
  }
  rows.sort((a, b) => a.at - b.at);
  const header = ['Date', 'Time', 'Workout', 'Source', 'Exercise', 'Set type', 'Set', 'Weight', 'Unit', 'Reps', 'Target reps', 'Duration (min)', 'Note'];
  return [header, ...rows.map((r) => r.cells)].map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n';
}

const TYPES = new Set<RepwiseEvent['type']>([
  'programme_started', 'workout_started', 'set_logged', 'set_corrected', 'set_voided', 'workout_finished', 'workout_cancelled', 'workout_deleted',
  'deload_accepted', 'deload_declined', 'break_restart_accepted', 'break_restart_skipped', 'rest_adjusted', 'rest_skipped', 'transition_continued', 'exercise_focused', 'extra_set_added', 'block_continued', 'exercise_swapped', 'next_date_set', 'exercise_bookmarked', 'added_weight_progress',
  'target_overridden', 'settings_updated', 'next_workout_set', 'workout_imported', 'units_changed', 'workout_noted', 'bodyweight_logged', 'plan_selected', 'session_lightened', 'plan_weight_set',
]);

const isEvent = (x: unknown): x is RepwiseEvent =>
  !!x && typeof x === 'object' && typeof (x as { id?: unknown }).id === 'string' && TYPES.has((x as { type: RepwiseEvent['type'] }).type) && typeof (x as { at?: unknown }).at === 'number';

/** Parse a Repwise backup: the app's JSON (an event array) or the automatic R2 backup ({ events: [{ payload }] }). */
export function parseBackup(text: string): { events: RepwiseEvent[]; skipped: number } {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error("This isn't a Repwise backup file.");
  }
  const raw = Array.isArray(data) ? data : Array.isArray((data as { events?: unknown })?.events) ? ((data as { events: unknown[] }).events.map((r) => (r as { payload?: unknown })?.payload ?? r)) : null;
  if (!raw) throw new Error("This isn't a Repwise backup file.");
  const seen = new Set<string>();
  const events: RepwiseEvent[] = [];
  let skipped = 0;
  for (const r of raw) {
    if (!isEvent(r) || seen.has(r.id)) {
      skipped++;
      continue;
    }
    seen.add(r.id);
    events.push(r);
  }
  if (!events.some((e) => e.type === 'programme_started')) throw new Error("This backup has no programme in it, so there's nothing to restore.");
  return { events, skipped };
}

/** Restore replays the backup in its own order, so it's only safe before any workout exists on this account. */
export function canRestoreInto(existing: readonly RepwiseEvent[]): boolean {
  return !existing.some((e) => e.type === 'workout_started' || e.type === 'workout_imported' || e.type === 'set_logged');
}
